import React, { useEffect, useState } from "react";
import { formatDisplayFileSize } from "../../../utils/formatFileSize.js";
import { buildPreviewAttachmentUrl } from "../../../utils/previewMeta.js";
import { sanitizeFileUrl } from "../../../utils/filePreview.js";
import { showError } from "../../../utils/toast.js";

function formatMailDate(raw = "") {
  const s = String(raw || "").trim();
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  try {
    return d.toLocaleString("de-CH", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return s;
  }
}

function MailMetaRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-sm">
      <span className="text-blue-200/70 shrink-0">{label}</span>
      <span className="text-blue-100/90 break-words min-w-0">{value}</span>
    </div>
  );
}

function isImageAttachment(a = {}) {
  if (a?.isImage === true) return true;
  const ct = String(a?.contentType || "").toLowerCase();
  if (ct.startsWith("image/")) return true;
  const ext = String(a?.extension || "").toLowerCase();
  return ["png", "jpg", "jpeg", "gif", "webp", "heic"].includes(ext);
}

export default function EmailPreviewBody({
  subject = "",
  from = "",
  to = "",
  cc = "",
  date = "",
  text = "",
  truncated = false,
  attachments = [],
  previewModule = "",
  fileId = null,
  projektTyp = "",
  projektId = null,
  entityId = null,
}) {
  const subjectLine = String(subject || "").trim();
  const displaySubject = subjectLine || "(Ohne Betreff)";
  const formattedDate = formatMailDate(date);
  const bodyText = String(text || "").trim();

  const [imagePreview, setImagePreview] = useState({
    open: false,
    url: "",
    name: "",
    loading: false,
  });

  useEffect(() => {
    return () => {
      if (imagePreview.url && imagePreview.url.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview.url);
      }
    };
  }, [imagePreview.url]);

  const closeImagePreview = () => {
    setImagePreview((prev) => {
      if (prev.url && prev.url.startsWith("blob:")) {
        URL.revokeObjectURL(prev.url);
      }
      return { open: false, url: "", name: "", loading: false };
    });
  };

  const openImageAttachment = async (a, idx) => {
    const index = Number.isInteger(Number(a?.index)) ? Number(a.index) : idx;
    const url = buildPreviewAttachmentUrl({
      module: previewModule,
      fileId,
      index,
      projektTyp,
      projektId,
      entityId,
    });
    const safe = sanitizeFileUrl(url);
    if (!safe) {
      showError("Anhang-Vorschau nicht verfügbar.");
      return;
    }

    closeImagePreview();
    setImagePreview({ open: true, url: "", name: a?.name || "Anhang", loading: true });

    try {
      const res = await fetch(safe, { method: "GET", credentials: "include" });
      if (!res.ok) {
        throw new Error(`ATTACHMENT_${res.status}`);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      setImagePreview({
        open: true,
        url: objectUrl,
        name: a?.name || "Anhang",
        loading: false,
      });
    } catch {
      setImagePreview({ open: false, url: "", name: "", loading: false });
      showError("Bild-Anhang konnte nicht geladen werden.");
    }
  };

  return (
    <div className="space-y-3 min-w-0" data-testid="email-preview-body">
      <h3 className="text-base sm:text-lg font-semibold text-blue-50 break-words leading-snug">
        {displaySubject}
      </h3>

      <div className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5 space-y-1.5">
        <MailMetaRow label="Von" value={from} />
        <MailMetaRow label="An" value={to} />
        <MailMetaRow label="CC" value={cc} />
        <MailMetaRow label="Datum" value={formattedDate} />
      </div>

      {truncated ? (
        <p className="text-xs text-amber-200/90">
          Der Nachrichtentext wurde gekürzt. Für den vollständigen Inhalt «Download» verwenden.
        </p>
      ) : null}

      <div className="rounded-xl border border-white/10 bg-white/5 overflow-hidden min-w-0">
        <pre className="p-3 text-sm text-blue-100/90 whitespace-pre-wrap break-words font-mono leading-relaxed">
          {bodyText || "(Kein darstellbarer Textinhalt)"}
        </pre>
      </div>

      {Array.isArray(attachments) && attachments.length > 0 ? (
        <div className="min-w-0">
          <div className="text-blue-100/90 font-medium mb-1.5 text-sm">Anhänge</div>
          <ul className="space-y-2">
            {attachments.map((a, idx) => {
              const key = `${idx}-${a?.name || "anhang"}`;
              const sizeLabel = formatDisplayFileSize(a?.size);
              const clickable = isImageAttachment(a) && previewModule && fileId;
              const label = `${a?.name || "Anhang"}${sizeLabel ? ` (${sizeLabel})` : ""}`;
              return (
                <li key={key} className="min-w-0">
                  {clickable ? (
                    <button
                      type="button"
                      onClick={() => openImageAttachment(a, idx)}
                      className="w-full text-left rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-blue-100/90 break-all hover:bg-white/10 hover:border-blue-400/30 transition"
                      data-testid="email-attachment-image"
                    >
                      {label}
                      <span className="block text-[11px] text-blue-300/60 mt-0.5 normal-case tracking-normal">
                        Tippen zum Anzeigen
                      </span>
                    </button>
                  ) : (
                    <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-blue-100/90 break-all">
                      {label}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {imagePreview.open ? (
        <div
          className="rounded-xl border border-white/10 bg-black/40 p-3 space-y-2"
          data-testid="email-attachment-image-preview"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm text-blue-100/90 break-all min-w-0">
              {imagePreview.name || "Bild"}
            </div>
            <button
              type="button"
              onClick={closeImagePreview}
              className="shrink-0 text-xs text-blue-300/80 hover:text-blue-100 underline underline-offset-2"
            >
              Schliessen
            </button>
          </div>
          {imagePreview.loading ? (
            <div className="text-sm text-blue-200/70 py-8 text-center">Bild wird geladen…</div>
          ) : imagePreview.url ? (
            <img
              src={imagePreview.url}
              alt={imagePreview.name || "Anhang"}
              className="max-h-[min(60vh,520px)] w-auto max-w-full mx-auto rounded-lg object-contain"
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
